import { AssistantsService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { DeleteDialog } from "./DeleteDialog";
import { EntityDialogTitle } from "./EntityDialogTitle";
import { DeleteUsageHistoryOption } from "./DeleteUsageHistoryOption";
import { useCanDeleteHistory } from "@/services/Storage/useCanDeleteHistory";
import { useCloseTabAndRedirect } from "@/components/Layouts/ToolLayout/currentTool";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { useState } from "react";
import {
  deleteResourceUsageHistory,
  HistoryDeletionError,
} from "@/services/Storage/HistoryRetention";

interface IAssistantDeleteDialog {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  assistantId: string;
  assistantName: string;
  assistantIcon?: string;
  isAppPublished: boolean;
}

export const AssistantDeleteDialog = ({
  isOpen,
  onOpenChange,
  assistantId,
  assistantName,
  assistantIcon,
  isAppPublished,
}: IAssistantDeleteDialog) => {
  const { spotGroup } = useSpotUser();
  const closeTabAndRedirect = useCloseTabAndRedirect();
  const queryClient = useQueryClient();
  const canDeleteHistory = useCanDeleteHistory();
  const [deleteHistory, setDeleteHistory] = useState(false);

  const { data: conversationsPage, isLoading: isLoadingCount } = useRemoteData(
    DATA_KEYS.CONVERSATIONS,
    {
      path: { group_id: spotGroup.id },
      query: {
        assistant_id: assistantId,
        type: "assistant",
        page: 1,
        size: 1,
      },
    },
    { enabled: isOpen && canDeleteHistory }
  );

  const { data: deployments } = useRemoteData(
    DATA_KEYS.LIST_DEPLOYMENTS,
    { path: { group_id: spotGroup.id, assistant_id: assistantId } },
    { enabled: isOpen }
  );
  const isDeployed =
    isAppPublished || !!deployments?.some((deployment) => deployment.is_active);

  const handleOpenChange = (open: boolean) => {
    if (!open) setDeleteHistory(false);
    onOpenChange(open);
  };

  const deleteAssistantMutation = useMutation({
    mutationFn: async ({ withHistory }: { withHistory: boolean }) => {
      // History first, for the same reason as the flow dialog: a failure after
      // the agent is gone leaves the user with no way to retry the purge.
      if (withHistory) {
        try {
          await deleteResourceUsageHistory(spotGroup.id, {
            resource_id: assistantId,
            resource_type: "assistant",
          });
        } catch (error: unknown) {
          throw new HistoryDeletionError(error);
        }
      }
      return AssistantsService.deleteAssistant({
        path: { group_id: spotGroup.id, assistant_id: assistantId },
      });
    },
    onSuccess: (_result, { withHistory }) => {
      SpotToast.success(
        withHistory ? "Agent and usage history deleted" : "Agent deleted"
      );
      handleOpenChange(false);
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.ASSISTANTS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKSHOP_RECENT],
      });
      if (withHistory) {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.CONVERSATIONS] });
      }
      closeTabAndRedirect(assistantId);
    },
    onError: (error: unknown) => {
      // The agent is untouched, so the dialog stays open for a retry.
      if (error instanceof HistoryDeletionError) {
        SpotToast.error(
          "Couldn't delete the usage history, so the agent was not deleted"
        );
        return;
      }
      SpotToast.error("Error deleting the agent");
      handleOpenChange(false);
    },
  });

  return (
    <DeleteDialog
      title={
        <EntityDialogTitle
          action="Delete"
          icon={assistantIcon || "coworker"}
          name={assistantName}
        />
      }
      text={
        isDeployed
          ? "This agent is currently deployed. Deleting it takes its deployments offline, and users immediately lose access. You can restore the agent later, but its deployments will stay offline."
          : "This action will delete this agent. You can restore it later."
      }
      extra={
        canDeleteHistory ? (
          <DeleteUsageHistoryOption
            checked={deleteHistory}
            onCheckedChange={setDeleteHistory}
            count={conversationsPage?.total}
            isLoadingCount={isLoadingCount}
            entryLabel="chats"
            isDisabled={deleteAssistantMutation.isPending}
          />
        ) : undefined
      }
      onDelete={() => {
        analytics_track(ANALYTICS_EVENTS.COWORKER_DELETE, {
          assistant_id: assistantId,
          is_app_published: isAppPublished,
          delete_history: deleteHistory,
        });
        if (deleteHistory) {
          analytics_track(ANALYTICS_EVENTS.USAGE_HISTORY_DELETED, {
            resource_type: "agent",
            resource_id: assistantId,
            count: conversationsPage?.total,
          });
        }

        deleteAssistantMutation.mutate({ withHistory: deleteHistory });
      }}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isLoading={deleteAssistantMutation.status === "pending"}
    />
  );
};
